import React, { memo } from 'react';
import { Box, Stack, Typography, Divider, Tooltip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.scss';
import { MasterPoolDetails } from '../../../HbtMasterPool/types';
import { OfferDateConfig } from '../OfferDate/types';
import { CmhcFlexIcon, CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { LanguageShort } from '@hobt/constants';
import {
  checkForApprovalDateRules,
  checkForOfferDateRules,
  getEmptyState,
  getInsuranceDurationInYears,
  hasValidInsuranceExpiryDate
} from '../../utils/masterPoolDetailsUtils';
import { formatRatioWithFourDigits } from 'Feature/V2/HbtPortfolioManagement/utils/formatRatioWithFourDigits';
import formatAmountWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import i18n from 'i18next';
import { formatDigits } from 'Feature/V2/HbtPortfolioManagement/utils/FormatDigits';
interface MasterPoolDataRowsProps {
  data: MasterPoolDetails[] | undefined;
  masterPoolAccountStatusCode: string;
  setOfferDateConfig: React.Dispatch<React.SetStateAction<OfferDateConfig>>;
  showApprovalDateModal: () => void;
  showCitadalDateModal: () => void;
  showApprovalOfferDateModal: () => void;
  closeDates: () => void;
  setIsOfferDate: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMasterPoolApprovalDateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const LATEST_OFFER_DATE_FOR_CITADAL_DATE = '2025-01-01';

const MasterPoolDataRowsComponent: React.FC<MasterPoolDataRowsProps> = ({
  data,
  masterPoolAccountStatusCode,
  setOfferDateConfig,
  showApprovalDateModal,
  showApprovalOfferDateModal,
  showCitadalDateModal,
  closeDates,
  setIsOfferDate,
  setIsMasterPoolApprovalDateModal
}) => {
  const { t } = useTranslation('HbtMasterPoolDetails');

  return (
    <Box className={styles.poolDataRows}>
      {data?.map((pool: MasterPoolDetails, index: number) => (
        <Box key={index} className={styles.leftColumnContainer}>
          {/* Left Column */}
          <Box className={styles.leftColumn}>
            {/* Lender Info */}
            <Box className={styles.lenderInfo}>
              <CmhcFlexIcon
                className={styles.iconLender}
                type="icon_lender"
                width={48}
                height={48}
              />
              <Box className={styles.lenderContainer}>
                <Box sx={{ width: '100%' }}>
                  <Typography className={styles.insuredName}>
                    {i18n.language === LanguageShort.English
                      ? pool.insuredLenderName.englishName
                      : pool.insuredLenderName.frenchName}
                  </Typography>

                  <Typography variant="body2" className={styles.insuredCode}>
                    {t('Insured-Approved-Lender-Code')}: {pool.insuredLenderCode}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Dates Section */}
            <Stack
              direction={{ xs: 'row' }}
              spacing={4}
              sx={{ width: '100%' }}
              className={styles.offerDateWrapper}
            >
              {/* Citadal Date */}
              {pool.requestReceiptDate > LATEST_OFFER_DATE_FOR_CITADAL_DATE && (
                <Box className={styles.offerDate}>
                  <Typography variant="body2" className={styles.dateHeadings}>
                    {t('Fraud-Detection-Date')}
                  </Typography>
                  <Box className={styles.offerDateContainer}>
                    <Typography className={styles.dateSubHeadings}>
                      {pool.fraudDetectionDate || getEmptyState()}
                    </Typography>
                    {
                      <Box className={styles.iconEdit}>
                        <IconButton
                          aria-label="clear search button"
                          onClick={() => {
                            const headerText = pool.fraudDetectionDate
                              ? t('Override-Fraud-Detection-Date')
                              : t('Fraud-Detection-Date');
                            const text = pool.fraudDetectionDate
                              ? t('Override-Fraud-Detection-Date')
                              : t('Enter-Fraud-Detection-Date');
                            setOfferDateConfig((prev: OfferDateConfig) => ({
                              ...prev,
                              headerText,
                              btnCancelText: t('Cancel'),
                              btnSubmitText: text,
                              labelText: t('Fraud-Detection-Date'),
                              date: pool.fraudDetectionDate,
                              submit: () => showCitadalDateModal(),
                              cancel: () => closeDates()
                            }));
                            setIsOfferDate(true);
                          }}
                        >
                          <CmhcIcon size={CmhcIconSize.SMALL} type="icon_edit" />
                        </IconButton>
                      </Box>
                    }
                  </Box>
                </Box>
              )}
              {/* Offer Date */}
              <Box className={styles.offerDate}>
                <Typography variant="body2" className={styles.dateHeadings}>
                  {t('Offer-Date')}
                </Typography>
                <Box className={styles.offerDateContainer}>
                  <Typography className={styles.dateSubHeadings}>
                    {pool.offerDate || getEmptyState()}
                  </Typography>
                  {pool.offerDate && checkForOfferDateRules(masterPoolAccountStatusCode) && (
                    <Box className={styles.iconEdit}>
                      <IconButton
                        aria-label="edit-offer-date"
                        onClick={() => {
                          setOfferDateConfig((prev: OfferDateConfig) => ({
                            ...prev,
                            headerText: t('Overide-Offer-Date-Text'),
                            btnCancelText: t('Cancel'),
                            btnSubmitText: t('Overide-Offer-Date-Text'),
                            labelText: t('Offer-Date'),
                            date: pool.offerDate,
                            submit: () => showApprovalOfferDateModal(),
                            cancel: () => closeDates()
                          }));
                          setIsOfferDate(true);
                        }}
                      >
                        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_edit" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Approval Date */}
              <Box className={styles.offerDate}>
                <Typography className={styles.dateHeadings}>{t('Approval-Date')}</Typography>
                <Box className={styles.offerDateContainer}>
                  <Typography className={styles.dateSubHeadings}>
                    {pool.approvalDate || getEmptyState()}
                  </Typography>
                  {pool.approvalDate && checkForApprovalDateRules(masterPoolAccountStatusCode) && (
                    <Box className={styles.iconEdit}>
                      <IconButton
                        aria-label="edit-approval-date"
                        onClick={() => {
                          setOfferDateConfig((prev: OfferDateConfig) => ({
                            ...prev,
                            headerText: t('Override-Approval-Date'),
                            btnCancelText: t('Cancel'),
                            submit: () => showApprovalDateModal(),
                            btnSubmitText: t('Override-Approval-Date'),
                            labelText: t('Approval-Date'),
                            date: pool.approvalDate,
                            cancel: () => closeDates()
                          }));
                          setIsOfferDate(true);
                        }}
                      >
                        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_edit" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Finalization Date */}
              <Box className={styles.offerDate}>
                <Typography variant="body2" className={styles.dateHeadings}>
                  {t('Finalization-Date')}
                </Typography>
                <Box className={styles.offerDateContainer}>
                  <Typography className={styles.dateSubHeadings}>
                    {pool.finalizedDate || getEmptyState()}
                  </Typography>
                </Box>
              </Box>

              {/* Term Insurance */}
              {hasValidInsuranceExpiryDate(pool.poolInsuranceExpiryDate, pool.termInsurance) && (
                <Box className={styles.termInsuranceContainer}>
                  <Box className={styles.termInsurance}>
                    <Typography variant="body2" className={styles.dateHeadings}>
                      {t('Pi-Portfolio-Management-Table-Column-TermInsurance')}
                    </Typography>
                    <Tooltip
                      title={t('Term-Insurance-Tool-Tip')}
                      placement="top-start"
                      arrow
                      slotProps={{
                        tooltip: {
                          className: styles.termInsuranceDetailsToolTip
                        },
                        arrow: {
                          className: styles.tooltipArrow
                        }
                      }}
                    >
                      <Box className={styles.inlineFlexPointer}>
                        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                      </Box>
                    </Tooltip>
                  </Box>

                  <Box className={styles.flexWrapAlignCenter}>
                    <Typography
                      className={styles.dateSubHeadings}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginRight: 1
                      }}
                    >
                      {getInsuranceDurationInYears(pool, t) ?? getEmptyState()}
                    </Typography>
                    <Box sx={{ width: '100%' }}>
                      <Typography>
                        <span className={styles.insuranceExpiryDate}>
                          {t('Expires')} {pool.poolInsuranceExpiryDate}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Divider */}
          {pool.requestType !== '1' && (
            <>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'block' } }} />

              {/* Right Column */}

              <Box
                component="dl"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  flex: 1,
                  pt: { xs: 2, md: 0 },
                  width: '100%'
                }}
              >
                {pool.adjustedClaimRate != null && (
                  <Box component="div" sx={{ display: 'flex' }}>
                    <Typography
                      component="dt"
                      variant="body2"
                      className={styles.dateHeadings}
                      sx={{ minWidth: '150px' }}
                    >
                      {t('Five-year-Adjusted-Claim-Rate')}
                    </Typography>
                    <Typography
                      component="dd"
                      className={styles.dateSubHeadings}
                      sx={{ ml: 0, flex: 1, textAlign: 'right' }}
                    >
                      {formatRatioWithFourDigits(
                        pool.adjustedClaimRate,
                        i18n.language,
                        getEmptyState()
                      )}
                    </Typography>
                  </Box>
                )}

                <Box
                  component="div"
                  sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}
                >
                  <Typography
                    component="dt"
                    variant="body2"
                    className={styles.dateHeadings}
                    sx={{ minWidth: '150px' }}
                  >
                    {t('Premium-Rate')}
                  </Typography>
                  <Typography
                    component="dd"
                    className={styles.dateSubHeadings}
                    sx={{ ml: 0, flex: 1, textAlign: { xs: 'left', md: 'right' } }}
                  >
                    {formatRatioWithFourDigits(
                      Number(pool.premiumRate),
                      i18n.language,
                      getEmptyState()
                    )}
                  </Typography>
                </Box>

                <Box
                  component="div"
                  sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}
                >
                  <Typography
                    component="dt"
                    className={styles.dateHeadings}
                    sx={{ minWidth: '150px' }}
                  >
                    {t('Total-Insured-Amount')}
                  </Typography>
                  <Typography
                    component="dd"
                    className={styles.dateSubHeadings}
                    sx={{ ml: 0, flex: 1, textAlign: { xs: 'left', md: 'right' } }}
                  >
                    {pool.totalInsuredLoanAmount !== null
                      ? formatAmountWithDollar(pool.totalInsuredLoanAmount, i18n.language)
                      : getEmptyState()}
                  </Typography>
                </Box>

                <Box
                  component="div"
                  sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}
                >
                  <Typography
                    component="dt"
                    variant="body2"
                    className={styles.dateHeadings}
                    sx={{ minWidth: '150px' }}
                  >
                    {t('Total-Insured-Loans')}
                  </Typography>
                  <Typography
                    component="dd"
                    className={styles.dateSubHeadings}
                    sx={{ ml: 0, flex: 1, textAlign: { xs: 'left', md: 'right' } }}
                  >
                    {formatDigits(pool.totalInsuredLoanCount, i18n.language) || getEmptyState()}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};
export const MasterPoolDataRows = memo(
  MasterPoolDataRowsComponent
) as typeof MasterPoolDataRowsComponent;
